import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/app/Header";
import JobCard from "@/components/app/JobCard";
import JobDetailSheet from "@/components/app/JobDetailSheet";
import { BookmarkSimple, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SavedJobs() {
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    loadSaved();
  }, []);

  const loadSaved = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobs/saved");
      setJobs(data.jobs.map(d => ({ ...d.job, is_saved: true })));
    } catch (e) {
      toast.error("Failed to load saved jobs");
    } finally {
      setLoading(false);
    }
  };

  const openJob = (job) => { setSelected(job); setSheetOpen(true); };

  const onAnalysed = (analysis) => {
    setJobs((prev) => prev.map((j) => (j.id === analysis.job_id ? { ...j, analysis } : j)));
    setSelected((prev) => (prev && prev.id === analysis.job_id ? { ...prev, analysis } : prev));
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <div className="mb-8">
          <span className="tick-label inline-flex items-center gap-1.5"><BookmarkSimple size={14} weight="bold"/> Saved Jobs</span>
          <h1 className="mt-2 font-display font-black text-4xl lg:text-5xl tracking-tighter">
            Your saved opportunities.
          </h1>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm p-5 h-56 animate-pulse bg-slate-50/50" />
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <div className="border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm p-12 text-center">
            <Warning size={28} weight="duotone" className="mx-auto text-ink-mute" />
            <h3 className="mt-4 font-display font-bold text-xl">No saved jobs yet</h3>
            <p className="mt-2 text-sm text-ink-soft max-w-md mx-auto">
              Jobs you save from the dashboard will appear here.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {jobs.map((j, i) => (
              <div key={j.id} className="animate-fade-up" style={{ animationDelay: `${Math.min(i * 30, 300)}ms` }}>
                <JobCard job={j} onOpen={openJob} onStateChange={loadSaved} />
              </div>
            ))}
          </div>
        )}
      </div>
      <JobDetailSheet open={sheetOpen} job={selected} onOpenChange={setSheetOpen} onAnalysed={onAnalysed} onStateChange={loadSaved} />
    </div>
  );
}
